'use client';

import { c as _c } from "react/compiler-runtime";
import './content-feed-comments.scss';
import React, { useEffect, useRef, useState } from 'react';
import { ButtonStyle } from '@sola/ark-core/atoms/button-style/button-style.tsx';
import { cn } from '@sola/ark-core/utils/helpers.ts';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.tsx';
import { getElapsed } from '@sola/utils/relativeTime.ts';
import { ContentFeedAvatar } from '../../atoms/content-feed-avatar/content-feed-avatar.tsx';
import { ContentFeedReactions } from '../content-feed-reactions/content-feed-reactions.tsx';
import { type CommentResolved, type UseContentFeedCommentsProps, getInitials, getTopReactions, getVisibleComments, hasNothingToRender, shouldShowSeeAll, useContentFeedComments } from './content-feed-comments.logic.ts';
export type { UseContentFeedCommentsProps } from './content-feed-comments.logic.ts';
export type { CommentResolved } from './content-feed-comments.logic.ts';

// ─── Comment Item ───────────────────────────────────────────────────────────

interface CommentItemProps {
  comment: CommentResolved;
  onReply?: (commentId: string) => void;
  isChild?: boolean;
  /** Parent entry id — reported as `content_id` when a comment is liked */
  entryId?: string;
  /** Parent entry title — reported as `content_title` when a comment is liked */
  contentTitle?: string;
  /** Parent entry landing-page URL — reported as `content_url` when a comment is liked */
  contentUrl?: string;
  t: (key: string, options?: Record<string, unknown>) => string;
}
const CommentItem: React.FC<CommentItemProps> = t0 => {
  const $ = _c(92);
  const {
    comment,
    onReply,
    isChild,
    entryId,
    contentTitle,
    contentUrl,
    t
  } = t0;
  let t1;
  if ($[0] !== comment.publishedAt) {
    t1 = getElapsed(comment.publishedAt);
    $[0] = comment.publishedAt;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const elapsed = t1;
  const formattedDate = typeof elapsed === "string" ? elapsed : undefined;
  const isPending = comment.status === "pendingModeration";
  const hasReplies = !isChild && !!comment.children && comment.children.length > 0;
  const [isRepliesExpanded, setIsRepliesExpanded] = useState(false);
  let t2;
  if ($[2] !== comment.reactions) {
    t2 = comment.reactions ?? [];
    $[2] = comment.reactions;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const [currentReactions, setCurrentReactions] = useState(t2);
  const [justReactedEmoji, setJustReactedEmoji] = useState(null);
  let lastReplier;
  let lastReplierInitials;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  if ($[4] !== comment.author.displayName || $[5] !== comment.author.photo?.url || $[6] !== comment.children || $[7] !== comment.id || $[8] !== comment.reactions || $[9] !== comment.text || $[10] !== contentTitle || $[11] !== contentUrl || $[12] !== currentReactions || $[13] !== entryId || $[14] !== formattedDate || $[15] !== hasReplies || $[16] !== isChild || $[17] !== isPending || $[18] !== isRepliesExpanded || $[19] !== justReactedEmoji || $[20] !== onReply || $[21] !== t) {
    const topReactions = getTopReactions(currentReactions);
    const name = comment.author.displayName || "";
    let t9;
    if ($[30] !== name) {
      t9 = getInitials(name);
      $[30] = name;
      $[31] = t9;
    } else {
      t9 = $[31];
    }
    const initials = t9;
    let t10;
    if ($[32] !== comment.children || $[33] !== hasReplies) {
      lastReplier = hasReplies ? comment.children.at(-1) as CommentResolved : null;
      t10 = getInitials(lastReplier?.author.displayName);
      $[32] = comment.children;
      $[33] = hasReplies;
      $[34] = lastReplier;
      $[35] = t10;
    } else {
      lastReplier = $[34];
      t10 = $[35];
    }
    lastReplierInitials = t10;
    if ($[36] !== isChild) {
      t7 = cn("ContentFeedComments__item flex gap-comfy-sm", {
        "ContentFeedComments__item--child": isChild
      });
      $[36] = isChild;
      $[37] = t7;
    } else {
      t7 = $[37];
    }
    const t11 = hasReplies && !isRepliesExpanded;
    let t12;
    if ($[38] !== t11) {
      t12 = cn("ContentFeedComments__avatar-col pt-snug-xl shrink-0 flex flex-col items-center gap-snug-md", {
        "ContentFeedComments__avatar-col--has-replies": t11
      });
      $[38] = t11;
      $[39] = t12;
    } else {
      t12 = $[39];
    }
    const t13 = comment.author.photo?.url;
    let t14;
    if ($[40] !== comment.author.displayName || $[41] !== initials || $[42] !== t13) {
      t14 = <ContentFeedAvatar src={t13} alt={comment.author.displayName} initials={initials} />;
      $[40] = comment.author.displayName;
      $[41] = initials;
      $[42] = t13;
      $[43] = t14;
    } else {
      t14 = $[43];
    }
    let t15;
    if ($[44] !== hasReplies || $[45] !== isRepliesExpanded) {
      t15 = hasReplies && !isRepliesExpanded && <div className="ContentFeedComments__connector-line" />;
      $[44] = hasReplies;
      $[45] = isRepliesExpanded;
      $[46] = t15;
    } else {
      t15 = $[46];
    }
    if ($[47] !== t12 || $[48] !== t14 || $[49] !== t15) {
      t8 = <div className={t12}>{t14}{t15}</div>;
      $[47] = t12;
      $[48] = t14;
      $[49] = t15;
      $[50] = t8;
    } else {
      t8 = $[50];
    }
    t3 = "flex-1 min-w-0 flex flex-col py-snug-xl pr-comfy-md gap-snug-sm";
    let t16;
    if ($[51] !== comment.author.displayName) {
      t16 = <span className="ContentFeedComments__author-name label-potent label-lg label-neutral-moderate truncate">{comment.author.displayName}</span>;
      $[51] = comment.author.displayName;
      $[52] = t16;
    } else {
      t16 = $[52];
    }
    let t17;
    if ($[53] !== formattedDate) {
      t17 = formattedDate && <span className="label-moderate label-md label-neutral-subtle">{formattedDate}</span>;
      $[53] = formattedDate;
      $[54] = t17;
    } else {
      t17 = $[54];
    }
    if ($[55] !== t16 || $[56] !== t17) {
      t4 = <div className="flex items-center gap-comfy-sm whitespace-nowrap">{t16}{t17}</div>;
      $[55] = t16;
      $[56] = t17;
      $[57] = t4;
    } else {
      t4 = $[57];
    }
    if ($[58] !== comment.text) {
      t5 = <p className="label-moderate label-lg label-neutral-moderate">{comment.text}</p>;
      $[58] = comment.text;
      $[59] = t5;
    } else {
      t5 = $[59];
    }
    t6 = !isPending && <div className="ContentFeedComments__actions-row flex items-center justify-between pt-snug-xl pb-snug-md"><div className="flex items-center gap-comfy-sm"><ContentFeedReactions entryId={entryId} commentId={comment.id} contentTitle={contentTitle} contentUrl={contentUrl} reactions={comment.reactions} onReactionsChange={setCurrentReactions} onJustReacted={setJustReactedEmoji} canReact={true} compact={true} />{!isChild && onReply && <button type="button" className="ContentFeedComments__action label-moderate label-md" onClick={() => onReply(comment.id)}><ArkIcon iconName="message-circle-01" size="16px" /><span>{t("my:contentFeed.reply")}</span></button>}</div>{topReactions.length > 0 && <div className="flex items-center gap-snug-sm">{topReactions.map(r => <span key={r.emoji} className={cn("ContentFeedComments__reaction-badge label-moderate label-md label-neutral-mild", {
          "ContentFeedComments__reaction-badge--just-added": r.emoji === justReactedEmoji
        })}>{r.emoji} {r.count}</span>)}</div>}</div>;
    $[4] = comment.author.displayName;
    $[5] = comment.author.photo?.url;
    $[6] = comment.children;
    $[7] = comment.id;
    $[8] = comment.reactions;
    $[9] = comment.text;
    $[10] = contentTitle;
    $[11] = contentUrl;
    $[12] = currentReactions;
    $[13] = entryId;
    $[14] = formattedDate;
    $[15] = hasReplies;
    $[16] = isChild;
    $[17] = isPending;
    $[18] = isRepliesExpanded;
    $[19] = justReactedEmoji;
    $[20] = onReply;
    $[21] = t;
    $[22] = lastReplier;
    $[23] = lastReplierInitials;
    $[24] = t3;
    $[25] = t4;
    $[26] = t5;
    $[27] = t6;
    $[28] = t7;
    $[29] = t8;
  } else {
    lastReplier = $[22];
    lastReplierInitials = $[23];
    t3 = $[24];
    t4 = $[25];
    t5 = $[26];
    t6 = $[27];
    t7 = $[28];
    t8 = $[29];
  }
  let t9;
  if ($[60] !== isPending || $[61] !== t) {
    t9 = isPending && <div className="ContentFeedComments__pending flex items-center gap-snug-md"><ArkIcon iconName="info-circle" size="16px" /><span className="label-moderate label-md label-neutral-mild">{t("my:contentFeed.awaitingModeration")}</span></div>;
    $[60] = isPending;
    $[61] = t;
    $[62] = t9;
  } else {
    t9 = $[62];
  }
  let t10;
  if ($[63] !== t3 || $[64] !== t4 || $[65] !== t5 || $[66] !== t6 || $[67] !== t9) {
    t10 = <div className={t3}>{t4}{t5}{t6}{t9}</div>;
    $[63] = t3;
    $[64] = t4;
    $[65] = t5;
    $[66] = t6;
    $[67] = t9;
    $[68] = t10;
  } else {
    t10 = $[68];
  }
  let t11;
  if ($[69] !== t10 || $[70] !== t7 || $[71] !== t8) {
    t11 = <div className={t7}>{t8}{t10}</div>;
    $[69] = t10;
    $[70] = t7;
    $[71] = t8;
    $[72] = t11;
  } else {
    t11 = $[72];
  }
  let t12;
  if ($[73] !== comment.children?.length || $[74] !== hasReplies || $[75] !== isRepliesExpanded || $[76] !== lastReplier || $[77] !== lastReplierInitials || $[78] !== t) {
    t12 = hasReplies && !isRepliesExpanded && <button type="button" className="ContentFeedComments__reply-summary" onClick={() => setIsRepliesExpanded(true)}><div className="ContentFeedComments__connector-elbow"><div className="ContentFeedComments__connector-elbow-line" /></div><div className="ContentFeedComments__reply-avatar-wrapper">{lastReplier && <ContentFeedAvatar src={lastReplier.author.photo?.url} alt={lastReplier.author.displayName} initials={lastReplierInitials} size="sm" />}</div><div className="ContentFeedComments__reply-text"><span className="label-potent label-lg label-neutral-moderate">{t("my:contentFeed.repliesCount", {
            count: comment.children?.length ?? 0
          })}</span><span className="label-moderate label-md label-neutral-subtle">{lastReplier ? getElapsed(lastReplier.publishedAt) : ""}</span></div></button>;
    $[73] = comment.children?.length;
    $[74] = hasReplies;
    $[75] = isRepliesExpanded;
    $[76] = lastReplier;
    $[77] = lastReplierInitials;
    $[78] = t;
    $[79] = t12;
  } else {
    t12 = $[79];
  }
  let t13;
  if ($[80] !== comment.children || $[81] !== contentTitle || $[82] !== contentUrl || $[83] !== entryId || $[84] !== hasReplies || $[85] !== isRepliesExpanded || $[86] !== t) {
    t13 = hasReplies && isRepliesExpanded && <div>{(comment.children as CommentResolved[]).map(child => <CommentItem key={child.id} comment={child} isChild={true} entryId={entryId} contentTitle={contentTitle} contentUrl={contentUrl} t={t} />)}</div>;
    $[80] = comment.children;
    $[81] = contentTitle;
    $[82] = contentUrl;
    $[83] = entryId;
    $[84] = hasReplies;
    $[85] = isRepliesExpanded;
    $[86] = t;
    $[87] = t13;
  } else {
    t13 = $[87];
  }
  let t14;
  if ($[88] !== t11 || $[89] !== t12 || $[90] !== t13) {
    t14 = <>{t11}{t12}{t13}</>;
    $[88] = t11;
    $[89] = t12;
    $[90] = t13;
    $[91] = t14;
  } else {
    t14 = $[91];
  }
  return t14;
};

// ─── Comment Input ──────────────────────────────────────────────────────────

interface CommentInputProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  maxLength: number;
  canSubmit: boolean;
  isSubmitting: boolean;
  placeholder: string;
  /** Reply context rendered inside the input box */
  replyNote?: React.ReactNode;
  /** Label for the submit button */
  submitLabel: string;
  /** Ref forwarded to the underlying textarea for programmatic focus */
  textareaRef?: React.RefObject<HTMLTextAreaElement | null>;
}
const CommentInput: React.FC<CommentInputProps> = t0 => {
  const $ = _c(44);
  const {
    value,
    onChange,
    onSubmit,
    maxLength,
    canSubmit,
    isSubmitting,
    placeholder,
    replyNote,
    submitLabel,
    textareaRef
  } = t0;
  const [isFocused, setIsFocused] = useState(false);
  const blurTimeoutRef = useRef(null);
  const charCount = value.length;
  const isOverLimit = charCount > maxLength;
  let t1;
  if ($[0] !== canSubmit || $[1] !== onSubmit) {
    t1 = e => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        if (canSubmit) {
          onSubmit();
        }
      }
    };
    $[0] = canSubmit;
    $[1] = onSubmit;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const handleKeyDown = t1;
  let t2;
  if ($[3] !== onChange) {
    t2 = e_0 => {
      onChange(e_0.target.value);
      const el = e_0.target;
      el.style.height = "auto";
      el.style.height = `${el.scrollHeight}px`;
    };
    $[3] = onChange;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const handleChange = t2;
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
        blurTimeoutRef.current = null;
      }
      setIsFocused(true);
    };
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const handleFocus = t3;
  let t4;
  if ($[6] !== textareaRef || $[7] !== value) {
    t4 = () => {
      blurTimeoutRef.current = setTimeout(() => {
        if (textareaRef?.current) {
          textareaRef.current.style.height = "";
        }
        if (!value) {
          setIsFocused(false);
        }
      }, 100);
    };
    $[6] = textareaRef;
    $[7] = value;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const handleBlur = t4;
  let t5;
  if ($[9] !== isFocused) {
    t5 = cn("ContentFeedComments__input-wrapper flex items-end gap-snug-md", {
      "ContentFeedComments__input-wrapper--focused": isFocused
    });
    $[9] = isFocused;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const t6 = !!replyNote;
  let t7;
  if ($[11] !== t6) {
    t7 = cn("ContentFeedComments__input", {
      "ContentFeedComments__input--has-reply": t6
    });
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== isFocused) {
    t8 = cn("ContentFeedComments__textarea", {
      "ContentFeedComments__textarea--expanded": isFocused
    });
    $[13] = isFocused;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== handleBlur || $[16] !== handleChange || $[17] !== handleKeyDown || $[18] !== isSubmitting || $[19] !== placeholder || $[20] !== t8 || $[21] !== textareaRef || $[22] !== value) {
    t9 = <textarea ref={textareaRef} className={t8} value={value} onChange={handleChange} onKeyDown={handleKeyDown} onFocus={handleFocus} onBlur={handleBlur} placeholder={placeholder} rows={1} disabled={isSubmitting} />;
    $[15] = handleBlur;
    $[16] = handleChange;
    $[17] = handleKeyDown;
    $[18] = isSubmitting;
    $[19] = placeholder;
    $[20] = t8;
    $[21] = textareaRef;
    $[22] = value;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== charCount || $[25] !== isFocused || $[26] !== isOverLimit || $[27] !== maxLength) {
    t10 = isFocused && <div className="flex justify-end"><span className={cn("label-sm", isOverLimit ? "label-negative" : "label-neutral-subtle")}>{charCount}/{maxLength}</span></div>;
    $[24] = charCount;
    $[25] = isFocused;
    $[26] = isOverLimit;
    $[27] = maxLength;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  let t11;
  if ($[29] !== t10 || $[30] !== t7 || $[31] !== t9) {
    t11 = <div className={t7}>{t9}{t10}</div>;
    $[29] = t10;
    $[30] = t7;
    $[31] = t9;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  let t12;
  if ($[33] !== replyNote || $[34] !== t11) {
    t12 = <div className="ContentFeedComments__input-column flex-1 flex flex-col min-w-0">{replyNote}{t11}</div>;
    $[33] = replyNote;
    $[34] = t11;
    $[35] = t12;
  } else {
    t12 = $[35];
  }
  const t13 = !canSubmit;
  let t14;
  if ($[36] !== onSubmit || $[37] !== submitLabel || $[38] !== t13) {
    t14 = <ButtonStyle variant="primary" size="sm"><button type="button" onClick={onSubmit} disabled={t13}>{submitLabel}</button></ButtonStyle>;
    $[36] = onSubmit;
    $[37] = submitLabel;
    $[38] = t13;
    $[39] = t14;
  } else {
    t14 = $[39];
  }
  let t15;
  if ($[40] !== t12 || $[41] !== t14 || $[42] !== t5) {
    t15 = <div className={t5}>{t12}{t14}</div>;
    $[40] = t12;
    $[41] = t14;
    $[42] = t5;
    $[43] = t15;
  } else {
    t15 = $[43];
  }
  return t15;
};

// ─── Presentational View ────────────────────────────────────────────────────

/**
 * Props for the dumb comments view. Mirrors the shape returned by
 * {@link useContentFeedComments} plus the parent-driven `toggleSignal`, so the
 * view can be rendered in Storybook/tests by passing plain values — no hook,
 * no data fetching.
 */
export interface ContentFeedCommentsViewProps extends ReturnType<typeof useContentFeedComments> {
  /** Incremented by the parent each time the Comment button is clicked. */
  toggleSignal?: number;
}

/**
 * Presentational comment section for content feed cards.
 * Renders a comment list with "See all" link and a text input for posting new
 * comments. Owns only view-local UI concerns (input focus, list expand/collapse,
 * reacting to the parent's toggle signal); all data + submission state is
 * supplied via props by {@link ContentFeedComments}.
 */
export const ContentFeedCommentsView: React.FC<ContentFeedCommentsViewProps> = t0 => {
  const $ = _c(41);
  const {
    toggleSignal: t1,
    entryId,
    contentTitle,
    contentUrl,
    comments,
    commentCount,
    inputText,
    setInputText,
    maxLength,
    canSubmit,
    canComment,
    isSubmitting,
    inputMode,
    replyToId,
    replyToName,
    handleSubmit,
    toggleComment,
    openReply,
    cancelReply,
    handleLoadAllComments,
    isLoadingAll,
    t
  } = t0;
  const toggleSignal = t1 === undefined ? 0 : t1;
  const textareaRef = useRef(null);
  const prevSignalRef = useRef(toggleSignal);
  let t2;
  let t3;
  if ($[0] !== toggleComment || $[1] !== toggleSignal) {
    t2 = () => {
      if (toggleSignal !== prevSignalRef.current) {
        prevSignalRef.current = toggleSignal;
        toggleComment();
      }
    };
    t3 = [toggleSignal, toggleComment];
    $[0] = toggleComment;
    $[1] = toggleSignal;
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  useEffect(t2, t3);
  const isInputVisible = inputMode !== "idle";
  let t4;
  if ($[4] !== isInputVisible) {
    t4 = () => {
      if (isInputVisible) {
        requestAnimationFrame(() => {
          textareaRef.current?.focus();
        });
      }
    };
    $[4] = isInputVisible;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== isInputVisible || $[7] !== replyToId) {
    t5 = [isInputVisible, replyToId];
    $[6] = isInputVisible;
    $[7] = replyToId;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  useEffect(t4, t5);
  const [isListExpanded, setIsListExpanded] = useState(false);
  if (!canComment) {
    return null;
  }
  if (hasNothingToRender({
    isInputVisible,
    isListExpanded,
    commentCount: comments.length
  })) {
    return null;
  }
  let t6;
  if ($[9] !== replyToId || $[10] !== t) {
    t6 = replyToId ? t("my:contentFeed.replyPlaceholder") : t("my:contentFeed.commentPlaceholder");
    $[9] = replyToId;
    $[10] = t;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const placeholder = t6;
  let t7;
  if ($[12] !== handleLoadAllComments) {
    t7 = () => {
      setIsListExpanded(true);
      handleLoadAllComments();
    };
    $[12] = handleLoadAllComments;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const handleSeeAll = t7;
  let t8;
  if ($[14] !== commentCount || $[15] !== comments || $[16] !== contentTitle || $[17] !== contentUrl || $[18] !== entryId || $[19] !== handleSeeAll || $[20] !== isListExpanded || $[21] !== isLoadingAll || $[22] !== openReply || $[23] !== t) {
    t8 = comments.length > 0 && <div className="ContentFeedComments__list flex flex-col gap-comfy-sm px-comfy-lg py-comfy-md">{getVisibleComments(comments, isListExpanded).map(comment => <CommentItem key={comment.id} comment={comment} onReply={openReply} entryId={entryId} contentTitle={contentTitle} contentUrl={contentUrl} t={t} />)}{shouldShowSeeAll(commentCount, comments.length, isListExpanded) && <div className="ContentFeedComments__see-all-wrapper"><button type="button" className="ContentFeedComments__see-all label-potent label-lg" onClick={handleSeeAll} disabled={isLoadingAll}>{isLoadingAll ? t("my:contentFeed.loading") : t("my:contentFeed.seeAllComments", {
            count: commentCount
          })}</button></div>}</div>;
    $[14] = commentCount;
    $[15] = comments;
    $[16] = contentTitle;
    $[17] = contentUrl;
    $[18] = entryId;
    $[19] = handleSeeAll;
    $[20] = isListExpanded;
    $[21] = isLoadingAll;
    $[22] = openReply;
    $[23] = t;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  let t9;
  if ($[25] !== canSubmit || $[26] !== cancelReply || $[27] !== handleSubmit || $[28] !== inputText || $[29] !== isInputVisible || $[30] !== isSubmitting || $[31] !== maxLength || $[32] !== placeholder || $[33] !== replyToId || $[34] !== replyToName || $[35] !== setInputText || $[36] !== t) {
    t9 = isInputVisible && <CommentInput textareaRef={textareaRef} value={inputText} onChange={setInputText} onSubmit={handleSubmit} maxLength={maxLength} canSubmit={canSubmit} isSubmitting={isSubmitting} placeholder={placeholder} replyNote={replyToId && replyToName ? <div className="ContentFeedComments__reply-note label-moderate label-md flex items-center"><ArkIcon iconName="corner-down-right" /><span className="ml-snug-lg">{t("my:contentFeed.replyingTo")}</span><span className="ml-snug-md flex-1 min-w-0 truncate">{replyToName}</span><button type="button" className="ContentFeedComments__reply-note-close" onClick={cancelReply} aria-label={t("my:contentFeed.cancelReply")}><ArkIcon iconName="x-close" /></button></div> : undefined} submitLabel={t("my:contentFeed.postComment")} />;
    $[25] = canSubmit;
    $[26] = cancelReply;
    $[27] = handleSubmit;
    $[28] = inputText;
    $[29] = isInputVisible;
    $[30] = isSubmitting;
    $[31] = maxLength;
    $[32] = placeholder;
    $[33] = replyToId;
    $[34] = replyToName;
    $[35] = setInputText;
    $[36] = t;
    $[37] = t9;
  } else {
    t9 = $[37];
  }
  let t10;
  if ($[38] !== t8 || $[39] !== t9) {
    t10 = <div className="ContentFeedComments flex flex-col">{t8}{t9}</div>;
    $[38] = t8;
    $[39] = t9;
    $[40] = t10;
  } else {
    t10 = $[40];
  }
  return t10;
};

// ─── Smart Wrapper ──────────────────────────────────────────────────────────

export interface ContentFeedCommentsProps extends UseContentFeedCommentsProps {
  /** Incremented by the parent each time the Comment button is clicked. */
  toggleSignal?: number;
}

/**
 * Comment section for content feed cards.
 * Smart wrapper: owns comment data + submission state via
 * {@link useContentFeedComments} and delegates rendering to
 * {@link ContentFeedCommentsView}.
 */
export const ContentFeedComments: React.FC<ContentFeedCommentsProps> = t0 => {
  const $ = _c(6);
  let props;
  let toggleSignal;
  if ($[0] !== t0) {
    ({
      toggleSignal,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
    $[2] = toggleSignal;
  } else {
    props = $[1];
    toggleSignal = $[2];
  }
  const commentsState = useContentFeedComments(props);
  let t1;
  if ($[3] !== commentsState || $[4] !== toggleSignal) {
    t1 = <ContentFeedCommentsView {...commentsState} toggleSignal={toggleSignal} />;
    $[3] = commentsState;
    $[4] = toggleSignal;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
};